import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Navbar } from '../Header/Navbar';
import { useAuthStore } from '../../store/authStore';
import { useFamilyStore } from '../../store/familyStore';

interface GalleryPhoto {
  id: string;
  r2Key: string;
  caption: string;
  location: string;
  year: string;
}

/**
 * Public gallery — read-only. Uploads/deletes happen from the admin
 * dashboard (Admin → Gallery tab), not here.
 */
export const Gallery: React.FC = () => {
  const { token } = useAuthStore();
  const { currentFamilyTreeId } = useFamilyStore();
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '');

  useEffect(() => {
    if (!currentFamilyTreeId || !token) return;
    fetch(`${apiBase}/gallery/${currentFamilyTreeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => setPhotos(Array.isArray(data) ? data : []))
      .catch(() => setPhotos([]));
  }, [currentFamilyTreeId, token, apiBase]);

  const currentIndex = photos.findIndex(p => p.id === selected);
  const current = currentIndex >= 0 ? photos[currentIndex] : null;

  const prev = () => setSelected(photos[(currentIndex - 1 + photos.length) % photos.length].id);
  const next = () => setSelected(photos[(currentIndex + 1) % photos.length].id);

  const photoUrl = (r2Key: string) => `${apiBase}/photos/${r2Key}`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Family Gallery</h1>
          <p className="text-gray-600">
            {photos.length} photo{photos.length !== 1 ? 's' : ''}
          </p>
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500">No photos yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group aspect-square overflow-hidden rounded-xl shadow-sm">
                <img
                  src={photoUrl(photo.r2Key)}
                  alt={photo.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                  onClick={() => setSelected(photo.id)}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                  <div className="p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-sm font-medium">{photo.caption || 'No caption'}</p>
                    <p className="text-xs text-white/80">{photo.year} {photo.location && `• ${photo.location}`}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox */}
      {selected && current && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <button
            onClick={() => setSelected(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={prev}
            className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <button
            onClick={next}
            className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          <div className="max-w-3xl w-full">
            <img
              src={photoUrl(current.r2Key)}
              alt={current.caption}
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="text-white text-center mt-4">
              <p className="text-lg font-medium">{current.caption || 'No caption'}</p>
              <p className="text-white/70 text-sm">
                {current.year} {current.location && `• ${current.location}`}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
